import React, { useState, useEffect, useRef } from 'react';
import { useAudio } from '../state/AudioContext';
import { useNavigate, useLocation } from 'react-router-dom';

export const MiniAudioPlayer = () => {
    const { currentItem, isPlaying, play, stop, rate, setPlaybackRate } = useAudio();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Position State (Movable)
    const [position, setPosition] = useState({ x: 0, y: 80 }); // Default: top center-ish
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{ startX: number, startY: number, startPosX: number, startPosY: number } | null>(null);
    const playerRef = useRef<HTMLDivElement>(null);

    // Persistence: Hide if on the same page as the item
    const isOnDetailPage = currentItem && (
        (currentItem.type === 'blog' && location.pathname === `/blog/${currentItem.id}`) ||
        (currentItem.type === 'product' && location.pathname === `/product/${currentItem.id}`)
    );

    // Initial centering
    useEffect(() => {
        if (typeof window !== 'undefined') {
            setPosition(prev => ({ ...prev, x: (window.innerWidth / 2) }));
        }
    }, []);

    if (!currentItem || isOnDetailPage) return null;

    // --- DRAG LOGIC ---
    const handleStart = (clientX: number, clientY: number) => {
        setIsDragging(true);
        dragRef.current = {
            startX: clientX,
            startY: clientY,
            startPosX: position.x,
            startPosY: position.y
        };
    };

    const handleMove = (clientX: number, clientY: number) => {
        if (!isDragging || !dragRef.current) return;
        
        const deltaX = clientX - dragRef.current.startX;
        const deltaY = clientY - dragRef.current.startY;
        
        let newX = dragRef.current.startPosX + deltaX;
        let newY = dragRef.current.startPosY + deltaY;

        // Boundaries
        const margin = 20;
        const playerWidth = playerRef.current?.offsetWidth || 300;
        newX = Math.max(playerWidth / 2 + margin, Math.min(window.innerWidth - playerWidth / 2 - margin, newX));
        newY = Math.max(margin + 60, Math.min(window.innerHeight - 120, newY));

        setPosition({ x: newX, y: newY });
    };

    const handleEnd = () => setIsDragging(false);

    // --- HANDLERS ---
    const handleSpeedToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        const speeds = [1.0, 1.5, 2.0];
        const nextIdx = (speeds.indexOf(rate) + 1) % speeds.length;
        setPlaybackRate(speeds[nextIdx]);
    };

    const handleContainerClick = () => {
        if (isDragging) return;
        const targetPath = currentItem.type === 'blog' ? `/blog/${currentItem.id}` : `/product/${currentItem.id}`;
        navigate(targetPath);
    };

    return (
        <div 
            ref={playerRef}
            className={`fixed z-[100] transition-shadow duration-300 touch-none ${isDragging ? 'scale-105 shadow-2xl' : 'shadow-xl'}`}
            style={{ 
                left: `${position.x}px`, 
                top: `${position.y}px`,
                transform: 'translate(-50%, 0)',
                width: 'calc(100% - 32px)',
                maxWidth: '360px'
            }}
            onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
            onMouseMove={(e) => handleMove(e.clientX, e.clientY)}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX, e.touches[0].clientY)}
            onTouchEnd={handleEnd}
        >
            {/* Glass Container */}
            <div 
                onClick={handleContainerClick}
                className="relative bg-white/10 dark:bg-black/20 backdrop-blur-2xl border border-white/20 dark:border-white/10 rounded-3xl p-2.5 pr-4 flex items-center gap-3 cursor-grab active:cursor-grabbing overflow-hidden"
            >
                {/* Visual Drag Handle Indicator */}
                <div className="absolute left-1 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-30">
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                </div>

                {/* Cover with Visualizer */}
                <div className="relative w-11 h-11 rounded-2xl bg-black/40 overflow-hidden shrink-0 border border-white/10 ml-1">
                    <img src={currentItem.imageUrl} alt="Cover" className="w-full h-full object-cover opacity-60" />
                    {isPlaying && (
                        <div className="absolute inset-0 flex items-end justify-center gap-0.5 pb-2">
                            <div className="w-1 bg-primary animate-[music-bar_1s_ease-in-out_infinite] h-2"></div>
                            <div className="w-1 bg-primary animate-[music-bar_1.2s_ease-in-out_infinite_0.1s] h-4"></div>
                            <div className="w-1 bg-primary animate-[music-bar_0.8s_ease-in-out_infinite_0.2s] h-3"></div>
                        </div>
                    )}
                </div>

                {/* Text Info */}
                <div className="flex-1 min-w-0 pointer-events-none">
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="material-symbols-outlined text-[10px] text-primary filled">
                            {currentItem.type === 'blog' ? 'article' : 'shopping_bag'}
                        </span>
                        <p className="text-text-main dark:text-white text-[9px] font-black uppercase tracking-widest opacity-70">
                            {currentItem.type === 'blog' ? 'Leyendo Artículo' : 'Escuchando Producto'}
                        </p>
                    </div>
                    <p className="text-text-main dark:text-white text-xs font-black truncate leading-tight">
                        {currentItem.title}
                    </p>
                </div>

                {/* Interactive Controls */}
                <div className="flex items-center gap-1.5 relative z-10" onClick={e => e.stopPropagation()}>
                    <button 
                        onClick={handleSpeedToggle}
                        className="size-8 flex items-center justify-center rounded-xl text-text-main dark:text-white hover:bg-white/10 transition-colors text-[10px] font-black"
                    >
                        {rate}x
                    </button>

                    <button 
                        onClick={() => play(currentItem)}
                        className="size-9 flex items-center justify-center rounded-xl bg-primary text-white shadow-lg shadow-primary/20 active:scale-90 transition-transform"
                    >
                        <span className="material-symbols-outlined filled text-xl">
                            {isPlaying ? 'pause' : 'play_arrow'}
                        </span>
                    </button>

                    <button 
                        onClick={() => stop()}
                        className="size-8 flex items-center justify-center rounded-xl text-text-main/40 dark:text-white/40 hover:text-red-500 transition-colors"
                    >
                        <span className="material-symbols-outlined text-lg">close</span>
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes music-bar {
                    0%, 100% { height: 4px; }
                    50% { height: 12px; }
                }
            `}</style>
        </div>
    );
};